/** JS mirror of the sizing tokens in tokens.css, for windowing math and tests. */
export const TV_VIEWPORT = { width: 1920, height: 1080 } as const;
export const SAFE_MARGIN = 48;
export const RAIL_WIDTH = 96;
/** Width of the rail while expanded as an overlay (docs/04 §7.1). */
export const RAIL_OVERLAY_WIDTH = 456;
export const CONTENT_X = 144;
export const CONTENT_WIDTH = 1728;
export const CARD_SIZE = 220;
export const CARD_GAP = 32;
export const ROW_GAP = 28;

/**
 * The card's text block, mirrored from `Card.module.css` (a contract test reads the CSS and
 * checks these numbers). Every card reserves exactly two title lines and one subtitle line,
 * whatever its copy, so a row's height is a constant known before render: a name that wraps
 * ("Supervisor de tus Sueños") can no longer push its "5 canciones" line under the next row.
 */
export const CARD_TEXT = {
  /** Gap between the cover and the title. */
  coverGap: 14,
  /** Title lines reserved on every card (clamped with an ellipsis past this). */
  titleLines: 2,
  /** Title line box per variant (px): song 28 px type, playlist 30 px type. */
  titleLine: { song: 35, playlist: 38 },
  /** Gap between the title block and the subtitle. */
  titleGap: 6,
  /** Subtitle ("5 canciones", artists): one line, ellipsised. */
  subtitleLine: 35,
} as const;

export type CardVariant = keyof typeof CARD_TEXT.titleLine;

/** Rendered height of a card (cover + two title lines + subtitle), in px. */
export function cardHeight(variant: CardVariant = 'song', size: number = CARD_SIZE): number {
  return (
    size +
    CARD_TEXT.coverGap +
    CARD_TEXT.titleLines * CARD_TEXT.titleLine[variant] +
    CARD_TEXT.titleGap +
    CARD_TEXT.subtitleLine
  );
}
/** Mirror of `--akp-focus-bleed`: slack a clipping container keeps around a focused element. */
export const FOCUS_BLEED = 24;

export const COLORS = {
  bg: '#0b0b14',
  bgVideo: '#07070e',
  surface: '#15152a',
  surface2: '#1e1e36',
  surface3: '#2b2b4a',
  playingBg: '#113844',
  text: '#f4f3fa',
  textMuted: '#9b97b8',
  white: '#ffffff',
  pink: '#ff2d87',
  cyan: '#19e3ff',
  purple: '#c65cff',
  blue: '#5b8cff',
  orange: '#ff7359',
  green: '#7ed957',
  teal: '#3ed6a5',
  amber: '#ffb33b',
} as const;
export type ColorToken = keyof typeof COLORS;

/** Flat cover colors used for items without artwork (Figma cards/rows). */
export const COVER_COLORS: readonly string[] = [
  COLORS.blue,
  COLORS.orange,
  COLORS.purple,
  COLORS.cyan,
  COLORS.pink,
  COLORS.green,
  COLORS.teal,
  COLORS.amber,
];

/** Deterministic flat color for a string (item id, user name…). */
export function colorFor(seed: string, palette: readonly string[] = COVER_COLORS): string {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return palette[h % palette.length] ?? palette[0] ?? COLORS.blue;
}
